import React from "react";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
// AdminLayout.js
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserData } from "../store/userActions";
import {
  Box,
  Flex,
  Text,
  IconButton,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  Link,
  CloseButton,
  Icon,
  Divider,
  useToast,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  FiMenu,
  FiHome,
  FiDollarSign,
  FiSettings,
  FiFileText,
  FiLogOut,
  FiX,
  FiRefreshCw,
  FiInfo,
  FiCreditCard,
  FiShield,
} from "react-icons/fi"; // Updated icon for Admin
import SessionValidator from "../auth/SessionValidator";
import DashboardFooter from "./DashboardFooter"; // Adjust the path as needed
import LogoArea from "./LogoArea";
import OrgSelector from "../components/OrgSelector";
import logout from "../auth/logout";
import { useNavigate } from "react-router-dom";

function AdminLayout() {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpen, onToggle } = useDisclosure();
  const bgColor = useColorModeValue("gray.100", "gray.900");

  const {
    stripe_dashboard_attention_required,
    stripe_requirements_pending_count,
    isSystemAdmin,
    email,
  } = useSelector((state) => state.user);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  useEffect(() => {
    setIsBannerVisible(!!stripe_dashboard_attention_required);
  }, [stripe_dashboard_attention_required]);

  const navItemsDefault = [
    { name: "Dashboard", icon: FiHome, route: "/dashboard" },
    { name: "Campaigns", icon: FiFileText, route: "/campaigns" },
    { name: "Fund Groups", icon: FiFileText, route: "/fund-groups" },
    { name: "Donations", icon: FiDollarSign, route: "/donations" },
    // { name: "Refunds", icon: FiCornerUpLeft, route: "/refunds" },
    { name: "Subscriptions", icon: FiRefreshCw, route: "/subscriptions" },
    { name: "Banking", icon: FiCreditCard, route: "/bank" },
    { name: "My Profile", icon: FiInfo, route: "/info" },
  ];

  // Additional admin-only navigation items
  const navItemsAdmin = [
    { name: "Settings", icon: FiSettings, route: "/settings" },
    { name: "Admin Panel", icon: FiShield, route: "/admin/view" },
    { name: "Tips A/B", icon: FiShield, route: "/tips" },
  ];

  return (
    <>
      <Flex direction={{ base: "column", md: "row" }} h="100vh">
        {/* Sidebar */}
        <Box
          w={{ base: "100%", md: "240px" }}
          bg={bgColor}
          p={4}
          borderRight={{ base: "none", md: "1px solid" }}
          borderRightColor={{ md: "gray.200" }}
          position={{ base: "fixed", md: "relative" }}
          zIndex={{ base: 1000, md: "auto" }}
          transition="transform 0.3s ease-in-out"
          transform={{
            base: isOpen ? "translateX(0)" : "translateX(-100%)",
            md: "none",
          }}
          height={{ base: "100vh", md: "100%" }}
        >
          <Flex direction="column" h="100%">
            <Flex justify="space-between" align="center">
              <LogoArea />
              <IconButton
                display={{ base: "block", md: "none" }}
                onClick={onToggle}
                icon={isOpen ? <FiX /> : <FiMenu />}
                variant="outline"
              />
            </Flex>

            <Divider my={4} />

            {/* Organization Selector */}
            <Box mb={4} display={{ base: "none", md: "block" }}>
              <OrgSelector isHeader={false} />
            </Box>

            <VStack align="stretch" spacing={3}>
              {navItemsDefault.map((item) => (
                <NavItem
                  key={item.name}
                  name={item.name}
                  icon={item.icon}
                  route={item.route}
                  onClick={onToggle} // Closes the menu on item click
                />
              ))}

              {/* Show admin-only items if user is admin */}
              {email &&
                (email === "malanchuksam@gmail.com" ||
                  email === "sammym771@gmail.com") && (
                  <>
                    <Divider />
                    {navItemsAdmin.map((item) => (
                      <NavItem
                        key={item.name}
                        name={item.name}
                        icon={item.icon}
                        route={item.route}
                        onClick={onToggle} // Closes the menu on item click
                      />
                    ))}
                  </>
                )}

              {/* Show System Admin section if user is system admin */}
              {isSystemAdmin && (
                <>
                  <Divider />
                  <NavItem
                    name="Platform Dashboard"
                    icon={FiShield}
                    route="/system-admin/organizations"
                    onClick={onToggle}
                  />
                </>
              )}

              <Divider />
              <NavItem
                name="Log Out"
                icon={FiLogOut}
                onClick={() => logout(toast, navigate)}
              />
            </VStack>
          </Flex>
        </Box>

        {/* Main Content */}
        <Box
          flex="1"
          overflowY="auto"
          p={4}
          ml={{ base: "0", md: "0" }}
          transition="margin-left 0.3s ease-in-out"
          position="relative" // Add this to position the alert relative to the content
        >
          {/* Mobile header */}
          <Box
            bg={bgColor}
            w="100%"
            p={4}
            display={{ base: "flex", md: "none" }}
            alignItems="center"
            justifyContent="space-between"
            borderBottom="1px solid"
            borderBottomColor="gray.200"
            position="fixed"
            top="0"
            left="0"
            right="0"
            zIndex="999"
          >
            <LogoArea />
            <HStack spacing={2}>
              <OrgSelector isHeader={true} />
              <IconButton
                onClick={onToggle}
                icon={isOpen ? <FiX /> : <FiMenu />}
                variant="outline"
              />
            </HStack>
          </Box>

          {/* Alert Banner inside Main Content */}
          {isBannerVisible && stripe_dashboard_attention_required && (
            <Alert
              status="warning"
              justifyContent="space-between"
              position="relative"
              zIndex="1000"
              mt={{ base: "56px", md: "0" }}
            >
              <AlertIcon />
              <Text flex="1" fontSize="sm">
                {Number(stripe_requirements_pending_count || 0) > 0
                  ? `Stripe needs ${stripe_requirements_pending_count} more item(s) on your connected account. Donations may fail until this is finished.`
                  : "Your donors cannot process payouts reliably until banking and verification are complete in Stripe."}
                <Link as={RouterLink} to="/bank" fontWeight="bold" ml={2}>
                  Open Banking / Continue in Stripe
                </Link>
              </Text>
              <CloseButton onClick={() => setIsBannerVisible(false)} />
            </Alert>
          )}

          {/* Actual content below the banner */}
          <Box mt={{ base: "80px", md: "0" }}>
            {" "}
            {/* Adjust margin-top to account for the alert and mobile header */}
            <SessionValidator />
            <Outlet />
          </Box>
          <DashboardFooter />
        </Box>
      </Flex>
    </>
  );
}

function NavItem({ name, icon, route, onClick }) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <RouterLink to={route} onClick={onClick}>
      <HStack
        spacing={3}
        align="center"
        p={2}
        pl={4}
        role="group"
        cursor="pointer"
        bg={pathname === route ? "gray.300" : "transparent"}
        _hover={{
          bg: useColorModeValue("gray.2 00", "gray.700"),
        }}
        rounded="md"
      >
        <Icon
          as={icon}
          boxSize={5}
          _groupHover={{
            color: "orange.400",
          }}
          color={pathname === route ? "orange.400" : "gray"}
        />
        <Text fontWeight="medium">{name}</Text>
      </HStack>
    </RouterLink>
  );
}

export default AdminLayout;
